import { MARKETPLACE_META, type CatalogImageCase, type ProductReviewDetail, type ReviewImage, type ReviewImageRendition } from "./types";

export const AMAZON_CATALOG_IMAGE_HELP_URL = "https://sellercentral.amazon.de/help/center?redirectSource=Hill";
export const DEFAULT_CATALOG_IMAGE_REASON = "Das Bild zeigt nicht den angebotenen Lieferumfang. Bitte dieses Katalogbild prüfen und entfernen oder durch das passende Einzelproduktbild ersetzen.";
const COMPLAINT_STORAGE_KEY = "playzeug.catalog-image-complaints.v1";
const MAX_SAVED_COMPLAINTS = 50;

function matchesCatalogUrl(image: ReviewImage, catalogImage: ReviewImage): boolean {
  return Boolean(image.url?.trim()) && (catalogImage.url === image.url || Boolean(catalogImage.renditions?.some(rendition => rendition.url === image.url)));
}

/** Source labels and readOnly flags are insufficient: the URL must occur in catalog evidence. */
export function matchingCatalogDetails(image: ReviewImage, details: ProductReviewDetail[], sellerSku?: string): ProductReviewDetail[] {
  return details.filter(detail => (!sellerSku || detail.sellerSku === sellerSku) && detail.images.catalog.some(catalogImage => matchesCatalogUrl(image, catalogImage)));
}

export function isCatalogImage(image: ReviewImage, details: ProductReviewDetail[], sellerSku?: string): boolean {
  return matchingCatalogDetails(image, details, sellerSku).length > 0;
}

/** Identity includes real catalog roles, ASINs and countries even when the gallery deduplicated an own image. */
export function catalogImageComplaintKey(image: ReviewImage, sellerSku: string, details: ProductReviewDetail[]): string {
  const matches = matchingCatalogDetails(image, details, sellerSku);
  if (!matches.length) return "";
  return JSON.stringify([sellerSku, image.url, matches.flatMap(detail => detail.images.catalog
    .filter(catalogImage => matchesCatalogUrl(image, catalogImage))
    .map(catalogImage => [detail.marketplaceCode, detail.asin ?? null, catalogImage.role, catalogImage.url ?? null, catalogImage.width ?? null, catalogImage.height ?? null,
      (catalogImage.renditions ?? []).map(rendition => [rendition.url, rendition.width ?? null, rendition.height ?? null])]))
    .sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right)))]);
}

/** A gallery scope may change while the complaint remains open in an individual country. */
function countryComplaintKeys(key: string): string[] {
  if (!key) return [];
  try {
    const identity: unknown = JSON.parse(key);
    if (Array.isArray(identity) && typeof identity[0] === "string" && typeof identity[1] === "string" && Array.isArray(identity[2]) && identity[2].every(Array.isArray)) {
      return identity[2].map(catalog => JSON.stringify({ sellerSku: identity[0], imageUrl: identity[1], catalog }));
    }
  } catch { /* Existing opaque keys remain isolated and cannot match a catalog identity. */ }
  return [key];
}

function savedComplaintKeys(): string[] {
  try {
    const saved: unknown = JSON.parse(localStorage.getItem(COMPLAINT_STORAGE_KEY) ?? "[]");
    return Array.isArray(saved) ? [...new Set(saved.filter((key): key is string => typeof key === "string").flatMap(countryComplaintKeys))].slice(-MAX_SAVED_COMPLAINTS) : [];
  } catch { return []; }
}

export function readCatalogImageComplaint(key: string): boolean {
  const saved = new Set(savedComplaintKeys());
  return countryComplaintKeys(key).some(country => saved.has(country));
}

export function writeCatalogImageComplaint(key: string, flagged: boolean): void {
  if (!key) return;
  try {
    const scope = new Set(countryComplaintKeys(key));
    const keys = savedComplaintKeys().filter(saved => !scope.has(saved));
    if (flagged) keys.push(...scope);
    localStorage.setItem(COMPLAINT_STORAGE_KEY, JSON.stringify(keys.slice(-MAX_SAVED_COMPLAINTS)));
  } catch { /* A blocked browser store must not prevent local support preparation. */ }
}

function imageAddress(image: ReviewImageRendition): string {
  const dimensions = image.width != null && image.height != null ? ` (${image.width} × ${image.height} Pixel)` : "";
  return `${image.url}${dimensions}`;
}

export interface CatalogImageSupportFields {
  request: string;
  steps: string;
  references: string;
  diagnostics: string;
}

/** Only actual SKU/country/catalog evidence can associate a persisted case with an image. */
export function matchingCatalogImageCases(image: ReviewImage, sellerSku: string, details: ProductReviewDetail[]): CatalogImageCase[] {
  const result = new Map<string, CatalogImageCase>();
  for (const detail of matchingCatalogDetails(image, details, sellerSku)) {
    const urls = new Set(detail.images.catalog.filter(candidate => matchesCatalogUrl(image, candidate)).flatMap(candidate => [candidate.url, ...(candidate.renditions ?? []).map(rendition => rendition.url)]).filter((url): url is string => Boolean(url)));
    for (const entry of detail.images.catalogCases ?? []) {
      if (entry.sellerSku !== sellerSku || entry.marketplaceCode !== detail.marketplaceCode || entry.asin !== detail.asin) continue;
      if (![entry.imageUrl, ...(entry.imageUrls ?? [])].some(url => urls.has(url))) continue;
      const previous = result.get(entry.id);
      if (!previous || previous.revision <= entry.revision) result.set(entry.id, entry);
    }
  }
  return [...result.values()];
}

export function catalogImageCaseActive(entry: CatalogImageCase): boolean {
  return ["waiting", "needs_check", "check_failed"].includes(entry.status);
}

/** Plain text mapped directly to the three Seller Central fields; raw diagnostics stay separate. */
export function buildCatalogImageSupportFields(input: Parameters<typeof buildCatalogImageSupportText>[0]): CatalogImageSupportFields {
  const { image, sellerSku, details, reason = DEFAULT_CATALOG_IMAGE_REASON } = input;
  const matches = matchingCatalogDetails(image, details, sellerSku);
  if (!matches.length) return { request: "", steps: "", references: "", diagnostics: "" };
  const request = [
    "Bitte prüfen Sie das folgende unpassende Katalogbild und entfernen Sie es oder ersetzen Sie es durch eine zum angebotenen Produkt passende Abbildung.",
    `Grund: ${reason.trim() || DEFAULT_CATALOG_IMAGE_REASON}`,
    `SKU: ${sellerSku}`,
  ];
  const references = [`SKU: ${sellerSku}`];
  for (const detail of matches) {
    const marketplace = MARKETPLACE_META[detail.marketplaceCode];
    request.push("", `${detail.marketplaceCode} – ${marketplace.label}`, `ASIN: ${detail.asin || "nicht in den aktuellen Daten angegeben"}`, `Artikel: ${detail.germanTitle || detail.title}`);
    const reported = detail.images.catalog.filter(candidate => matchesCatalogUrl(image, candidate));
    const reportedUrls = new Set(reported.flatMap(candidate => [candidate.url, ...(candidate.renditions ?? []).map(rendition => rendition.url)]));
    for (const candidate of reported) request.push(`Beanstandetes Katalogbild ${candidate.role}: ${candidate.url}`);
    const own = detail.images.current.filter(candidate => candidate.url && !reportedUrls.has(candidate.url) && (candidate.role === "MAIN" || /^PT\d{2}$/.test(candidate.role)));
    if (own.length) request.push("Diese eigenen Bildbeiträge bitte beibehalten:", ...own.map(candidate => `${candidate.role}: ${candidate.url}`));
    references.push(`${detail.marketplaceCode}: ASIN ${detail.asin || "nicht angegeben"} · Marktplatz-ID ${marketplace.marketplaceId}`);
    if (detail.images.submissionId) references.push(`${detail.marketplaceCode}: Amazon-Übermittlungs-ID ${detail.images.submissionId}`);
  }
  return {
    request: request.join("\n"),
    steps: "Die aktuell von Amazon bereitgestellten Angebots- und Katalogbilddaten wurden abgerufen. Die Bildadressen des beanstandeten Katalogbilds und der eigenen Bildbeiträge wurden für diese Anfrage zusammengestellt.",
    references: references.join("; "),
    diagnostics: buildCatalogImageSupportText(input),
  };
}

export function buildCatalogImageSupportText({ image, sellerSku, details, reason = DEFAULT_CATALOG_IMAGE_REASON, preparedAt = new Date().toISOString() }: {
  image: ReviewImage;
  sellerSku: string;
  details: ProductReviewDetail[];
  reason?: string;
  preparedAt?: string;
}): string {
  const matches = matchingCatalogDetails(image, details, sellerSku);
  if (!matches.length) return "";
  const lines = [
    `Betreff: Unpassendes Katalogbild prüfen – SKU ${sellerSku}`,
    "",
    "Support-Text vorbereitet – noch nicht an Amazon gesendet.",
    `SKU: ${sellerSku}`,
    `Belegstand / Vorbereitung: ${preparedAt}`,
    "",
    `Beanstandung: ${reason.trim() || DEFAULT_CATALOG_IMAGE_REASON}`,
    "Bitte das nachfolgend bezeichnete Katalogbild prüfen und entfernen oder durch das passende Einzelproduktbild ersetzen.",
    "Die aufgeführten eigenen MAIN- und Zusatzbild-Beiträge bitte beibehalten. Diese Beiträge sollen durch diese Anfrage nicht entfernt werden.",
  ];
  for (const detail of matches) {
    const marketplace = MARKETPLACE_META[detail.marketplaceCode];
    lines.push("", `${detail.marketplaceCode} – ${marketplace.label}`, `Marktplatz-ID: ${marketplace.marketplaceId}`, `ASIN: ${detail.asin || "nicht in den aktuellen Daten angegeben"}`, `Artikel: ${detail.title}`);
    for (const catalogImage of detail.images.catalog.filter(candidate => matchesCatalogUrl(image, candidate))) {
      lines.push(`Beanstandetes Katalogbild ${catalogImage.role}: ${imageAddress({ ...catalogImage, url: catalogImage.url! })}`);
      for (const rendition of catalogImage.renditions ?? []) lines.push(`Von Amazon gelieferte Bildfassung: ${imageAddress(rendition)}`);
    }
    const preserved = detail.images.current.filter(candidate => candidate.url && (candidate.role === "MAIN" || /^PT\d{2}$/.test(candidate.role)));
    lines.push("Eigene Bildbeiträge beibehalten:");
    if (preserved.length) for (const contribution of preserved) lines.push(`${contribution.role}: ${imageAddress({ ...contribution, url: contribution.url! })}`);
    else lines.push("Keine eigenen MAIN-/PT-Bildadressen in den aktuellen Daten angegeben.");
    const state = detail.images;
    lines.push(`Aktueller Prüfstatus des eigenen Beitrags: ${state.status}`);
    if (state.submissionId) lines.push(`Amazon-Übermittlungs-ID: ${state.submissionId}`);
    if (state.submittedAt) lines.push(`Von Amazon zur Verarbeitung angenommen: ${state.submittedAt}`);
    if (state.verifiedAt) lines.push(`Eigener Beitrag durch Rücklesen bestätigt: ${state.verifiedAt}`);
    if (state.lastCheckedAt) lines.push(`Letzter Amazon-Abgleich: ${state.lastCheckedAt}`);
    if (state.message) lines.push(`Statusmeldung: ${state.message}`);
    if (state.lastCheckError) lines.push(`Letzter Abgleichfehler: ${state.lastCheckError}`);
    for (const [label, issues] of [["Aktuelle Amazon-Meldungen", state.amazonIssues ?? detail.amazonIssues], ["Amazon-Verarbeitungsfehler", state.processingErrors], ["Amazon-Meldungen zur Übermittlung", state.submissionIssues]] as const) {
      if (issues?.length) lines.push(`${label}: ${JSON.stringify(issues, null, 2)}`);
    }
    if (state.verificationDifferences?.length) lines.push(`Abweichungen beim Rücklesen des eigenen Beitrags: ${JSON.stringify(state.verificationDifferences, null, 2)}`);
  }
  return lines.join("\n");
}
